import { logger } from './logger';
import { supabase } from '@/lib/supabase/client';

export const networkDiagnostics = {
  async testSupabaseConnection(): Promise<{
    url: string;
    reachable: boolean;
    error?: string;
    dns?: string;
    suggestion?: string;
  }> {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    
    if (!supabaseUrl) {
      return {
        url: 'Not configured',
        reachable: false,
        error: 'VITE_SUPABASE_URL is not set in environment variables',
        suggestion: 'Check your .env file and ensure VITE_SUPABASE_URL is set'
      };
    }

    try {
      // 1) Use a HEAD on anon REST endpoint instead of auth health (avoids 401 noise)
      const healthController = new AbortController();
      const healthTimeout = setTimeout(() => healthController.abort(), 4000);
      const anonHead = import.meta.env.VITE_SUPABASE_ANON_KEY;
      const head = await fetch(`${supabaseUrl}/rest/v1/cities?select=id&limit=1`, {
        method: 'HEAD',
        headers: anonHead ? { apikey: anonHead, Authorization: `Bearer ${anonHead}` } : {},
        signal: healthController.signal
      });
      clearTimeout(healthTimeout);
      if (head.ok) {
        return { url: supabaseUrl, reachable: true, dns: 'Resolved successfully' };
      }

      // 2) Lightweight HEAD using supabase-js with table head select (respects RLS)
      const { error: headError } = await supabase
        .from('cities')
        .select('id', { head: true, count: 'exact' });
      if (!headError) {
        return { url: supabaseUrl, reachable: true, dns: 'Resolved successfully' };
      }

      // 3) As last resort, attempt a single-row GET (may fail if RLS blocks anon)
      const getController = new AbortController();
      const getTimeout = setTimeout(() => getController.abort(), 5000);
      const anon = import.meta.env.VITE_SUPABASE_ANON_KEY;
      const res = await fetch(`${supabaseUrl}/rest/v1/cities?select=id&limit=1`, {
        method: 'GET',
        headers: anon ? { apikey: anon, Authorization: `Bearer ${anon}` } : {},
        signal: getController.signal
      });
      clearTimeout(getTimeout);
      if (res.ok) {
        return { url: supabaseUrl, reachable: true, dns: 'Resolved successfully' };
      }

      throw new Error(`HTTP ${res.status}`);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      
      let suggestion = 'Unknown issue';
      
      if (errorMessage.includes('ERR_NAME_NOT_RESOLVED')) {
        suggestion = 'DNS resolution failed. Try: 1) Clear DNS cache (ipconfig /flushdns), 2) Change DNS to 8.8.8.8, 3) Check VPN/firewall settings';
      } else if (errorMessage.includes('ERR_NETWORK_CHANGED')) {
        suggestion = 'Network changed during request. Check your internet connection stability';
      } else if (errorMessage.includes('ERR_INTERNET_DISCONNECTED')) {
        suggestion = 'No internet connection detected';
      } else if (errorMessage.includes('AbortError')) {
        suggestion = 'Request timed out. Supabase might be slow or blocked by firewall';
      }
      
      return {
        url: supabaseUrl,
        reachable: false,
        error: errorMessage,
        suggestion
      };
    }
  },

  async diagnoseAndLog(): Promise<{
    url: string;
    reachable: boolean;
    error?: string;
    dns?: string;
    suggestion?: string;
  }> {
    logger.info('=== Running Supabase Network Diagnostics ===');
    
    const result = await this.testSupabaseConnection();
    
    logger.info('Supabase URL:', result.url);
    logger.info('Reachable:', result.reachable);
    
    if (!result.reachable) {
      logger.error('Connection Error:', result.error);
      logger.warn('Suggestion:', result.suggestion);
      
      if (typeof window !== 'undefined') {
        logger.info('Browser:', navigator.userAgent);
        logger.info('Online:', navigator.onLine);
      }
    } else {
      logger.info('Connection successful!');
    }
    
    logger.info('=== End Diagnostics ===');
    
    return result;
  }
};

if (typeof window !== 'undefined') {
  window.addEventListener('error', (event) => {
    if (event.error?.message?.includes('ERR_NAME_NOT_RESOLVED') && 
        event.error?.message?.includes('supabase')) {
      logger.error('Supabase DNS error detected, running diagnostics...');
      networkDiagnostics.diagnoseAndLog();
    }
  });
  
  (window as any).diagnoseSupabase = () => networkDiagnostics.diagnoseAndLog();
} 